import { DIGITAL_FOOTPRINT_LITE_REPORT_TYPES } from '@ballerine/common';
import type {
  MerchantReportType,
  MerchantCategoryCodeSummary,
  MerchantReportRiskLevel,
  PolicyFitStatus,
} from '@ballerine/common';
import { Bell, BellOff, Check } from 'lucide-react';
import React from 'react';

import { ctw } from '@/common';
import { Card, CopyButton } from '@/components';
import { WarningFilledSvg } from '@/components/atoms';
import { Separator } from '@/components/atoms/Separator';
import {
  PolicyFitHeader,
  PolicyFitBadgePremium,
  QuestionMarkTooltip,
  Hexagon,
} from '../../report-shared/components';
import { riskTypeMapper } from '../../report-shared/constants';

export { riskTypeMapper };

type HeaderSectionProps = {
  reportId?: string;
  homepageScreenshotUrl?: string | null;
  riskLevel?: MerchantReportRiskLevel | null;
  lineOfBusiness?: string | null;
  mcc?: MerchantCategoryCodeSummary | null;
  geographies?: string[] | null;
  riskTypes?: Record<string, { status: 'clear' | 'alert' } | null> | null;
  policyFit?: {
    status?: PolicyFitStatus;
    description?: string | null;
    name?: string | null;
    version?: number | null;
  } | null;
  customer?: {
    displayName?: string | null;
  } | null;
  isPolicyFitEnabled?: boolean;
  isMonitoringReport: boolean;
  isAlert?: boolean;
  FeedbackComponent?: React.ComponentType<{ reportId: string }>;
  reportType: MerchantReportType;
};

export const HeaderSection: React.FC<HeaderSectionProps> = ({
  reportId,
  homepageScreenshotUrl,
  riskLevel,
  lineOfBusiness,
  mcc,
  geographies,
  riskTypes,
  policyFit,
  customer,
  isPolicyFitEnabled,
  isMonitoringReport,
  isAlert = false,
  FeedbackComponent,
  reportType,
}) => {
  const mccText = [mcc?.primary?.mcc, mcc?.primary?.description].filter(Boolean).join(' - ');
  const isLiteReport = DIGITAL_FOOTPRINT_LITE_REPORT_TYPES.includes(
    reportType as (typeof DIGITAL_FOOTPRINT_LITE_REPORT_TYPES)[number],
  );

  return (
    <Card className="overflow-hidden mb-6 rounded-3xl bg-white">
      <div className="flex flex-col">
        {homepageScreenshotUrl && (
          <div className="relative h-80 bg-gray-100">
            <a
              href={homepageScreenshotUrl}
              target="_blank"
              rel="noreferrer"
              className="block w-full h-full cursor-pointer"
              title="Click to view full screenshot"
            >
              <img
                src={homepageScreenshotUrl}
                alt="Homepage Screenshot"
                className="w-full h-full object-cover object-top opacity-80"
                onError={e => {
                  const target = e.target as HTMLImageElement;
                  const container = target.parentElement?.parentElement;

                  if (container) {
                    container.style.display = 'none';
                  }
                }}
              />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/10 to-black/40" />
                <div
                  className="absolute inset-0"
                  style={{
                    backdropFilter: 'blur(14px)',
                    WebkitBackdropFilter: 'blur(14px)',
                    maskImage:
                      'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.45) 50%, rgba(0,0,0,1) 100%)',
                    WebkitMaskImage:
                      'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.45) 50%, rgba(0,0,0,1) 100%)',
                  }}
                />
              </div>
            </a>
            {isMonitoringReport && (
              <div className="absolute bottom-6 text-sm left-6 z-10">
                <div className="px-2 py-1 bg-white shadow-lg rounded text-sm font-medium hover:bg-gray-50 transition-colors z-10 flex items-center gap-2">
                  {isAlert ? (
                    <>
                      <div className="relative">
                        <Bell className="h-4 w-4 text-neutral-900" />
                        <div className="absolute right-[1.5px] top-[1.5px] h-[6px] w-[6px] rounded-full border border-white bg-red-400" />
                      </div>
                      <span className="text-sm text-neutral-500">Changes Detected</span>
                    </>
                  ) : (
                    <>
                      <BellOff className="h-4 w-4 text-neutral-900" />
                      <span className="text-sm text-neutral-500">No Changes</span>
                    </>
                  )}
                </div>
              </div>
            )}
            {FeedbackComponent && reportId ? (
              <div className="absolute bottom-4 right-4 z-10">
                <FeedbackComponent reportId={reportId} />
              </div>
            ) : (
              <a
                href={`mailto:support@ballerine.com?subject=${encodeURIComponent(`Feedback for Report ${reportId || ''}`)}&body=${encodeURIComponent(`Report ID: ${reportId || 'Unknown'}\n\nFeedback:\n`)}`}
                className="absolute bottom-4 right-4 px-3 py-2 bg-white border border-gray-200 rounded text-sm font-medium text-zinc-950 hover:bg-gray-50 transition-colors z-10"
              >
                Contact Support
              </a>
            )}
          </div>
        )}

        <div className="bg-white flex gap-6">
          <div
            className={ctw('flex', {
              'flex-1': !isLiteReport,
            })}
          >
            <div
              className={ctw(
                `py-6 text-center flex items-center flex-col gap-2 flex-none min-w-48 ${riskTypes && !isLiteReport ? 'w-1/3' : 'w-full'}`,
              )}
            >
              <h4 className="text-sm font-semibold text-center">RISK LEVEL</h4>
              <Hexagon riskLevel={riskLevel} />
            </div>

            {!isLiteReport && riskTypes && <Separator orientation="vertical" />}

            {!isLiteReport && riskTypes && (
              <div className="py-6 flex-none w-2/3 pl-8">
                <div className="flex items-center gap-1 mb-3">
                  <h4 className="text-sm font-semibold text-stone-400">RISK TYPES</h4>

                  <QuestionMarkTooltip
                    text="Specific risk categories assessed including chargebacks, content analysis, reputation, transaction patterns, and legal considerations."
                    className="ml-2"
                  />
                </div>
                <div className="space-y-2">
                  {Object.keys(riskTypeMapper).map(name => {
                    if (!riskTypes[name]) {
                      return null;
                    }

                    const { status } = riskTypes[name];
                    const { Icon, title } = riskTypeMapper[name as keyof typeof riskTypeMapper];

                    return (
                      <div key={name} className="flex items-center gap-2">
                        {status === 'clear' ? (
                          <div className="w-5 h-5 rounded-full bg-green-500/10 flex items-center justify-center">
                            <Check className="h-2.5 w-2.5 text-green-500" strokeWidth={6} />
                          </div>
                        ) : (
                          <div className="w-5 h-5 flex items-center justify-center">
                            <WarningFilledSvg className="h-6 w-6" />
                          </div>
                        )}

                        <div
                          className={ctw(
                            'flex items-center gap-2 text-sm',
                            status === 'clear' ? 'text-stone-400' : 'text-slate-900',
                          )}
                        >
                          <Icon className="h-4 w-4" strokeWidth={1} />
                          <span>{title}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {!isLiteReport && (
            <div className="flex">
              <Separator orientation="vertical" />
            </div>
          )}

          <div className="py-6 flex gap-10 flex-1 justify-between">
            {policyFit && isPolicyFitEnabled ? (
              <PolicyFitHeader policyFit={policyFit} customer={customer} />
            ) : (
              <PolicyFitBadgePremium />
            )}
            <div className="flex-1">
              <div className="flex items-center gap-1 mb-3">
                <h4 className="text-sm font-semibold text-stone-400">GENERAL INFO</h4>
                <QuestionMarkTooltip
                  text="Basic merchant information including business type, merchant category code, and geographical scope."
                  className="ml-2"
                />
              </div>
              <div className="space-y-2 text-sm">
                {lineOfBusiness && (
                  <div>
                    <span className="font-bold text-slate-900">Line Of Business</span>
                    <br />
                    <span className="text-black">{lineOfBusiness}</span>
                  </div>
                )}
                {mccText && (
                  <div>
                    <span className="font-bold text-slate-900">MCC</span>
                    <br />
                    <div className="flex items-center gap-2">
                      <span className="text-black">{mccText}</span>
                      <CopyButton textToCopy={mccText} />
                    </div>
                  </div>
                )}
                {geographies && (
                  <div>
                    <span className="font-bold text-slate-900">Geographies</span>
                    <br />
                    <span className="text-black">{geographies.join(', ')}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};
